import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import AppBar from "../../../components/atoms/appbar";
import {
  useTournamentApplyListInfinite,
  useTournamentCancelListInfinite,
} from "../../../@apis/hooks/useSetting";
import { formatDate, formatDateRange } from "../../../lib/utils/dateUtils";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import Button from "../../../components/atoms/button";
import * as React from "react";
import type {
  TournamentApplyListItemResponse,
  TournamentCancelItem,
  TournamentCancelStatusLabel,
} from "../../../@apis/dto/response/torunament.response";

const TAB_LABELS = [
  { label: "접수/결제", value: "1" },
  { label: "취소/환불", value: "2" },
];

// 접수/결제 상태 뱃지 (myStampLabel)
const stampBadge = (label?: string | null) => {
  switch (label) {
    case "접수완료":
      return {
        label,
        className: "bg-[#f2ffec] text-[#34c759]",
        hasCheck: true,
      };
    case "승인대기":
    case "결제대기":
      return {
        label,
        className: "bg-[#fff4d8] text-[#a2845e]",
        hasCheck: false,
      };
    case "참가완료":
      return { label, className: "bg-[#8e8e93] text-white", hasCheck: true };
    case "미입금 마감":
      return {
        label,
        className: "bg-[#ffecec] text-[#ff3b30]",
        hasCheck: false,
      };
    case "접수취소":
      return { label, className: "bg-[#f2f2f7] text-black", hasCheck: false };
    default:
      return { label, className: "bg-[#8e8e93] text-white", hasCheck: false };
  }
};

// 취소/환불 상태 뱃지 (cancelStatusLabel)
const cancelBadge = (label?: TournamentCancelStatusLabel | null) => {
  switch (label) {
    case "환불완료":
      return { label, className: "bg-[#f2ffec] text-[#34c759]" };
    case "환불실패":
    case "결제실패":
    case "결제미완료":
      return { label, className: "bg-[#ffecec] text-[#ff3b30]" };
    case "접수취소":
      return { label, className: "bg-[#f2f2f7] text-black" };
    default:
      return label ? { label, className: "bg-[#f2f2f7] text-black" } : null;
  }
};

// 라벨(회색 w-80) + 값(검정) 한 줄
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex gap-[8px] items-start text-sm font-medium">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      <p className="text-black break-all whitespace-pre-line">{value}</p>
    </div>
  );
}

// 날짜별 그룹핑
function groupByDate<T>(items: T[], keyFn: (item: T) => string) {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(item);
  }
  return Array.from(map.entries());
}

export default function CompetitionHistory() {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [tab, setTab] = React.useState<string>(() => {
    // 다른 화면에서 탭을 지정해 들어온 경우(예: 접수 취소 완료 → 취소/환불 탭)
    const requestedTab = (location.state as { tab?: string } | null)?.tab;
    if (requestedTab) return requestedTab;

    // 뒤로가기(POP)로 들어온 경우: 저장된 탭이 있으면 그걸 사용
    if (navigationType === "POP") {
      const saved = sessionStorage.getItem("reservationHistoryTab");
      // 없으면 기본값 "1"
      return saved ?? "1";
    }

    // PUSH / REPLACE 인 경우: 새로 들어온 걸로 보고 항상 1로 초기화
    sessionStorage.removeItem("reservationHistoryTab"); // 혹시 남아있던 값 제거
    return "1";
  });

  // 상세에서 뒤로 돌아왔을 때 필터/스크롤/페이지 복원
  const cameFromDetailRef = React.useRef(
    sessionStorage.getItem("tournament_history_came_from_detail") === "true",
  );
  const savedScrollRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("scroll_tournament_history") ?? "0")
      : 0,
  );
  const savedPagesRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("tournament_history_pages") ?? "1")
      : 1,
  );
  const scrollRestoredRef = React.useRef(false);
  const restoreCleanupRef = React.useRef<(() => void) | null>(null);
  // 복원 진입 시 최초 1회 스크롤 초기화를 건너뛴다.
  const skipNextScrollResetRef = React.useRef(cameFromDetailRef.current);

  // 접수/결제 탭 필터: 전체 / 참가완료만 보기 (복원 시 저장값 사용)
  const [joinedOnly, setJoinedOnly] = React.useState(
    cameFromDetailRef.current
      ? sessionStorage.getItem("tournament_history_joined") === "true"
      : false,
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!cameFromDetailRef.current) {
      sessionStorage.removeItem("scroll_tournament_history");
      sessionStorage.removeItem("tournament_history_pages");
      sessionStorage.removeItem("tournament_history_joined");
    }
    sessionStorage.removeItem("tournament_history_came_from_detail");
  }, []);

  React.useEffect(() => {
    if (tab === "1") {
      queryClient.removeQueries({
        queryKey: ["resve-cancel-list"],
        exact: false,
      });
    }

    if (tab === "2") {
      queryClient.removeQueries({ queryKey: ["resve-list"], exact: false });
    }

    if (containerRef.current) {
      // 상세 복귀 최초 렌더에서는 스크롤 초기화를 건너뛰고 복원에 맡긴다.
      if (skipNextScrollResetRef.current) {
        skipNextScrollResetRef.current = false;
      } else {
        containerRef.current.scrollTop = 0;
      }
    }
  }, [tab, queryClient]);

  // 접수/결제 내역 (onlyJoined=true 면 참가완료만) — 무한 스크롤
  const {
    data: applyData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...useTournamentApplyListInfinite(joinedOnly),
    enabled: tab === "1",
  });

  const {
    data: cancelData,
    hasNextPage: hasNextCancelPage,
    isFetchingNextPage: isFetchingNextCancelPage,
    fetchNextPage: fetchNextCancelPage,
  } = useInfiniteQuery({
    ...useTournamentCancelListInfinite(),
    enabled: tab === "2",
  });

  const list =
    applyData?.pages.flatMap((page) => page.data?.applyList ?? []) ?? [];
  const cancelList =
    cancelData?.pages.flatMap((page) => page.data?.cancelList ?? []) ?? [];

  // 무한 스크롤 감지
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;

        if (tab === "1" && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
        if (tab === "2" && hasNextCancelPage && !isFetchingNextCancelPage) {
          fetchNextCancelPage();
        }
      },
      { root: containerRef.current, rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [
    tab,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasNextCancelPage,
    isFetchingNextCancelPage,
    fetchNextCancelPage,
  ]);

  // 활성 탭 기준 페이지/로딩 상태 (접수·취소 공통 복원용)
  const pages = applyData?.pages ?? [];
  const cancelPages = cancelData?.pages ?? [];
  const activePages = tab === "2" ? cancelPages : pages;
  const activeFirstItems =
    tab === "2" ? cancelPages[0]?.data?.cancelList : pages[0]?.data?.applyList;
  const activeHasNext = tab === "2" ? hasNextCancelPage : hasNextPage;
  const activeIsFetching =
    tab === "2" ? isFetchingNextCancelPage : isFetchingNextPage;
  const activeFetchNext = tab === "2" ? fetchNextCancelPage : fetchNextPage;

  // 복원 1) 저장된 페이지 수만큼 로드
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (activePages.length >= savedPagesRef.current) return;
    if (!activeHasNext || activeIsFetching) return;
    activeFetchNext();
  }, [
    tab,
    activePages.length,
    activeHasNext,
    activeIsFetching,
    activeFetchNext,
  ]);

  // 복원 2) 충분한 페이지가 로드되면 스크롤 위치 복원
  const enoughPagesLoaded =
    activePages.length >= savedPagesRef.current || !activeHasNext;
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (!enoughPagesLoaded || !activeFirstItems?.length) return;

    scrollRestoredRef.current = true;

    const saved = savedScrollRef.current;
    const el = containerRef.current;
    if (!saved || !el) return;

    const apply = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          el.scrollTop = saved;
        }),
      );

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    const timer = setTimeout(() => observer.disconnect(), 800);
    restoreCleanupRef.current = () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [tab, enoughPagesLoaded, activeFirstItems?.length]);

  // 언마운트 시에만 복원 옵저버 정리
  React.useEffect(() => () => restoreCleanupRef.current?.(), []);

  // 상세 진입 전 목록 상태(스크롤/페이지/필터/탭) 저장
  const saveHistoryState = () => {
    if (containerRef.current) {
      sessionStorage.setItem(
        "scroll_tournament_history",
        String(Math.floor(containerRef.current.scrollTop)),
      );
    }
    sessionStorage.setItem(
      "tournament_history_pages",
      String(activePages.length),
    );
    sessionStorage.setItem("tournament_history_joined", String(joinedOnly));
    sessionStorage.setItem("reservationHistoryTab", tab);
    sessionStorage.setItem("tournament_history_came_from_detail", "true");
  };

  // 접수/결제 카드
  const renderApplyCard = (tour: TournamentApplyListItemResponse) => {
    const badge = stampBadge(tour.myStampLabel ?? tour.tournamentLabel);

    return (
      <div
        onClick={() => {
          saveHistoryState();
          navigate(
            `/setting/tournament-history/${tour.id}/${tour.apply.id}`,
            joinedOnly ? { state: { fromJoined: true } } : undefined,
          );
        }}
        className="bg-white border border-[#d1d1d6] rounded-[10px] px-[16px] py-[12px] flex flex-col gap-[12px]"
      >
        {/* 상태 뱃지 + 이동 */}
        <div className="flex items-center justify-between">
          <div className="flex gap-[8px] items-center">
            {badge && (
              <span
                className={`px-[10px] py-[2.5px] inline-flex items-center gap-[5px] rounded-[5px] text-xs font-medium ${badge.className}`}
              >
                {badge.hasCheck && (
                  <svg
                    width="0.75rem"
                    height="0.75rem"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="shrink-0"
                  >
                    <path
                      d="M13.3307 4L5.9974 11.3333L2.66406 8"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {badge.label}
              </span>
            )}
          </div>
          <img src="/icons/ic-go.webp" alt="이동 아이콘" className="w-2 h-3" />
        </div>

        <div className="h-px w-full bg-[#e5e5ea]" />

        <p className="text-base font-semibold text-black break-keep">
          {tour.name}
        </p>

        <div className="flex flex-col gap-[8px]">
          {/* 라운드 대회는 여러 날에 걸치므로 대회 전체 기간, 일정 대회는 신청한 날짜 */}
          <DetailRow
            label="대회날짜"
            value={
              tour.tournamentType === "MULTI_ROUND"
                ? formatDateRange(tour.startDt, tour.endDt, "YYYY-MM-DD (ddd)")
                : tour.calendarInfo?.date
                  ? formatDate(tour.calendarInfo.date, "YYYY-MM-DD (ddd)")
                  : "-"
            }
          />
          <DetailRow
            label="대회일정"
            value={tour.calendarInfo?.timeType || "-"}
          />
          <DetailRow label="대회장소" value={tour.golfParkName || "-"} />
        </div>
      </div>
    );
  };

  // 취소/환불 카드
  const renderCancelCard = (tour: TournamentCancelItem) => {
    const badge = cancelBadge(tour.cancelStatusLabel);

    return (
      <div
        onClick={() => {
          saveHistoryState();
          // 접수/결제 상세와 동일한 화면에서 취소·환불 정보까지 노출한다.
          navigate(
            `/setting/tournament-history/${tour.tournamentId}/${tour.applyId}`,
          );
        }}
        className="bg-white border border-[#d1d1d6] rounded-[10px] px-[16px] py-[12px] flex flex-col gap-[12px]"
      >
        <div className="flex items-center justify-between">
          {badge && (
            <span
              className={`px-[10px] py-[2.5px] grid place-items-center rounded-[5px] text-xs font-medium ${badge.className}`}
            >
              {badge.label}
            </span>
          )}
          <img
            src="/icons/ic-go.webp"
            alt="이동 아이콘"
            className="w-2 h-3 ml-auto"
          />
        </div>

        <div className="h-px w-full bg-[#e5e5ea]" />

        <p className="text-base font-semibold text-black break-keep">
          {tour.tournamentName}
        </p>

        <div className="flex flex-col gap-[8px]">
          {/* 라운드 대회는 여러 날에 걸치므로 대회 전체 기간, 일정 대회는 신청한 날짜 */}
          <DetailRow
            label="대회날짜"
            value={
              tour.tournamentType === "MULTI_ROUND"
                ? formatDateRange(tour.startDt, tour.endDt, "YYYY-MM-DD (ddd)")
                : tour.calendarDate
                  ? formatDate(tour.calendarDate, "YYYY-MM-DD (ddd)")
                  : "-"
            }
          />
          <DetailRow
            label="대회일정"
            value={
              tour.calendarTimeType === "morning"
                ? "오전"
                : tour.calendarTimeType === "afternoon"
                  ? "오후"
                  : tour.calendarTimeType || "-"
            }
          />
          <DetailRow label="대회장소" value={tour.golfParkName || "-"} />
          <DetailRow
            label="취소유형"
            value={<b>{tour.cancelLabel || "-"}</b>}
          />
        </div>
      </div>
    );
  };

  const applyGroups = groupByDate(list, (tour) =>
    formatDate(
      tour.apply.insertDt || tour.calendarInfo.date || "",
      "YYYY.MM.DD (ddd)",
    ),
  );
  const cancelGroups = groupByDate(cancelList, (tour) =>
    formatDate(tour.cancelDt || "", "YYYY.MM.DD (ddd)"),
  );

  const isEmpty = tab === "2" ? cancelList.length === 0 : list.length === 0;

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto h-full relative flex flex-col bg-white"
    >
      <AppBar backButton title="대회 접수 내역" />

      <div className="flex border-b border-[#e5e5ea]">
        {TAB_LABELS.map((t) => {
          const isActive = t.value === tab;
          return (
            <button
              key={t.value}
              onClick={() => {
                setTab(t.value);
                sessionStorage.setItem("reservationHistoryTab", t.value);
              }}
              className="flex-1 relative py-[12px] text-sm font-medium text-center"
            >
              <span className={isActive ? "text-black" : "text-[#8e8e93]"}>
                {t.label}
              </span>
              {isActive && (
                <span className="absolute -bottom-[1px] left-0 w-full h-[1px] bg-black" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 flex flex-col bg-[#f2f2f7] p-[20px] gap-[12px]">
        {/* 접수/결제 탭 필터 */}
        {tab === "1" && (
          <div className="flex gap-[8px] items-start">
            <button
              onClick={() => setJoinedOnly(false)}
              className={`px-[20px] py-[8px] rounded-full text-sm font-medium ${
                joinedOnly
                  ? "bg-white border border-[#8e8e93] text-[#8e8e93]"
                  : "bg-black text-white"
              }`}
            >
              전체
            </button>
            <button
              onClick={() => setJoinedOnly(true)}
              className={`px-[20px] py-[8px] rounded-full text-sm font-medium ${
                joinedOnly
                  ? "bg-black text-white"
                  : "bg-white border border-[#8e8e93] text-[#8e8e93]"
              }`}
            >
              참가완료만 보기
            </button>
          </div>
        )}

        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <p className="text-sm font-medium text-center text-[#8e8e93]">
              {tab === "2"
                ? "취소/환불 내역이 없습니다."
                : joinedOnly
                  ? "참가 완료 내역이 없습니다."
                  : "접수/결제 내역이 없습니다."}
            </p>
          </div>
        ) : tab === "2" ? (
          <section className="flex flex-col gap-[12px]">
            {cancelGroups.map(([date, items]) => (
              <div key={date} className="flex flex-col gap-[8px]">
                <p className="text-xl font-medium text-black">{date}</p>
                <div className="flex flex-col gap-[12px]">
                  {items.map((tour) => (
                    <React.Fragment
                      key={`${tour.tournamentId}-${tour.applyId}`}
                    >
                      {renderCancelCard(tour)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            <div ref={loadMoreRef} className="h-[1px]" />
            {isFetchingNextCancelPage && (
              <p className="py-[12px] text-center text-sm text-[#8e8e93]">
                불러오는 중...
              </p>
            )}
          </section>
        ) : (
          <section
            key={joinedOnly ? "joined" : "all"}
            className="flex flex-col gap-[12px]"
          >
            {applyGroups.map(([date, items]) => (
              <div key={date} className="flex flex-col gap-[8px]">
                <p className="text-xl font-medium text-black">{date}</p>
                <div className="flex flex-col gap-[12px]">
                  {items.map((tour) => (
                    <React.Fragment key={`${tour.id}-${tour.apply.id}`}>
                      {renderApplyCard(tour)}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            {/* 무한 스크롤 감지 & 로딩 */}
            <div ref={loadMoreRef} className="h-[1px]" />
            {isFetchingNextPage && (
              <p className="py-[12px] text-center text-sm text-[#8e8e93]">
                불러오는 중...
              </p>
            )}
          </section>
        )}
      </div>

      {/* 접수/결제 내역이 없을 때 하단 대회 목록 보기 버튼 */}
      {isEmpty && tab === "1" && (
        <div className="bg-white p-[12px]">
          <Button
            type="button"
            className="w-full font-normal"
            variant={"black"}
            label="대회 목록 보기"
            onClick={() => navigate("/competition")}
          />
        </div>
      )}
    </div>
  );
}
